const SUCCESS_STATE_DURATION = 1800;
const ERROR_STATE_DURATION = 2500;
const pendingVariantIds = new Set();
const transientVariantStates = new Map();

/**
 * @param {string} variantId
 * @param {number} [expiresAt]
 */
function clearTransientVariantState(variantId, expiresAt) {
  const transientState = transientVariantStates.get(variantId);
  if (!transientState || (expiresAt && transientState.expiresAt !== expiresAt)) return;

  window.clearTimeout(transientState.cleanupTimer);
  transientVariantStates.delete(variantId);
}

/**
 * Handles form-safe product actions inside predictive search results.
 * Direct additions use Shopify's standard storefront action because the
 * result cards already live inside the search form.
 */
class PredictiveSearchProductActions extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number | undefined} */
  #resetTimer;

  #pending = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    const addButton = this.querySelector('[data-predictive-search-product-action="add"]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    const variantId = addButton.dataset.variantId || '';
    const transientState = transientVariantStates.get(variantId);

    if (pendingVariantIds.has(variantId)) {
      this.#setButtonState(addButton, 'loading');
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
    } else if (transientState && transientState.expiresAt > Date.now()) {
      this.#setButtonState(addButton, transientState.state);
      this.#announce(transientState.message, addButton);
      this.#scheduleReset(addButton, transientState.expiresAt - Date.now(), variantId, transientState.expiresAt);
    } else if (transientState) {
      clearTransientVariantState(variantId);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    if (this.#resetTimer) window.clearTimeout(this.#resetTimer);
  }

  /**
   * Keeps action keystrokes out of the parent search form's result-navigation
   * handler while allowing Escape and Tab to retain their dialog behaviour.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-predictive-search-product-action]')) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.stopPropagation();
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const action = target.closest('[data-predictive-search-product-action]');
    if (!(action instanceof HTMLElement) || !this.contains(action)) return;

    event.stopPropagation();

    if (!(action instanceof HTMLButtonElement) || action.dataset.predictiveSearchProductAction !== 'add') return;

    event.preventDefault();
    this.#addToCart(action);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (this.#resetTimer) {
      window.clearTimeout(this.#resetTimer);
      this.#resetTimer = undefined;
    }

    const variantId = button.dataset.variantId;
    const parsedQuantity = Number.parseInt(button.dataset.quantity || '1', 10);
    const quantity = Number.isFinite(parsedQuantity) ? Math.max(1, parsedQuantity) : 1;
    const actions = window.Shopify?.actions;

    if (!variantId || typeof actions?.updateCart !== 'function') {
      this.#showError(button, button.dataset.labelError || 'Error adding to cart');
      return;
    }

    if (this.#pending || pendingVariantIds.has(variantId)) return;

    clearTransientVariantState(variantId);
    this.#pending = true;
    pendingVariantIds.add(variantId);
    this.#setButtonState(button, 'loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const result = await actions.updateCart(
        {
          lines: [{ merchandiseId: variantId, quantity }],
        },
        {
          event: {
            context: 'dialog',
            detail: {
              source: 'predictive-search',
              productId: this.dataset.productId,
            },
          },
        }
      );

      if (result?.userErrors?.length) {
        throw new Error(result.userErrors[0]?.message || button.dataset.labelError || 'Error adding to cart');
      }

      const currentButton = this.#findCurrentButton(variantId, button);
      const successMessage = button.dataset.labelSuccess || 'Added to cart';
      const warningMessage = result?.warnings?.[0]?.message;
      const announcement = warningMessage ? `${successMessage}. ${warningMessage}` : successMessage;
      this.#showTransientState(currentButton, variantId, 'success', announcement, SUCCESS_STATE_DURATION);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.warn('[predictive-search] Unable to add product to cart:', error);
      const message = error instanceof Error ? error.message : button.dataset.labelError || 'Error adding to cart';
      this.#showError(this.#findCurrentButton(variantId, button), message, variantId);
    } finally {
      this.#pending = false;
      pendingVariantIds.delete(variantId);
      const currentButton = this.#findCurrentButton(variantId, button);
      currentButton.disabled = false;
      currentButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Cart updates can morph the header and replace the search result DOM. Resolve
   * the current button so loading and success feedback survive that refresh.
   * @param {string} variantId
   * @param {HTMLButtonElement} fallback
   * @returns {HTMLButtonElement}
   */
  #findCurrentButton(variantId, fallback) {
    if (fallback.isConnected) return fallback;

    const buttons = document.querySelectorAll(
      '[data-predictive-search-product-action="add"][data-variant-id]'
    );
    const currentButton = Array.from(buttons).find(
      (candidate) =>
        candidate instanceof HTMLButtonElement &&
        candidate.dataset.variantId === variantId &&
        candidate.offsetParent !== null
    );

    return currentButton instanceof HTMLButtonElement ? currentButton : fallback;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {'idle' | 'loading' | 'success' | 'error'} state
   */
  #setButtonState(button, state) {
    const label = button.querySelector('[data-predictive-search-add-label]');
    const labelKey = `label${state.charAt(0).toUpperCase()}${state.slice(1)}`;
    const nextLabel = button.dataset[labelKey] || button.dataset.labelIdle || '';

    button.dataset.state = state;
    if (label) label.textContent = nextLabel;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} message
   * @param {string} [variantId]
   */
  #showError(button, message, variantId) {
    if (variantId) {
      this.#showTransientState(button, variantId, 'error', message, ERROR_STATE_DURATION);
      return;
    }

    this.#setButtonState(button, 'error');
    this.#announce(message, button);
    this.#scheduleReset(button, ERROR_STATE_DURATION);
  }

  /**
   * Persists brief feedback across Shopify's header morph after a cart update.
   * @param {HTMLButtonElement} button
   * @param {string} variantId
   * @param {'success' | 'error'} state
   * @param {string} message
   * @param {number} duration
   */
  #showTransientState(button, variantId, state, message, duration) {
    clearTransientVariantState(variantId);
    const expiresAt = Date.now() + duration;
    const cleanupTimer = window.setTimeout(() => clearTransientVariantState(variantId, expiresAt), duration + 100);
    transientVariantStates.set(variantId, { state, message, expiresAt, cleanupTimer });
    this.#setButtonState(button, state);
    this.#announce(message, button);
    this.#scheduleReset(button, duration, variantId, expiresAt);
  }

  /**
   * @param {string} message
   * @param {HTMLButtonElement} [button]
   */
  #announce(message, button) {
    const actionGroup = button?.closest('predictive-search-product-actions') || this;
    const status = actionGroup.querySelector('[data-predictive-search-action-status]');
    if (status) status.textContent = message;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {number} delay
   * @param {string} [variantId]
   * @param {number} [expiresAt]
   */
  #scheduleReset(button, delay, variantId, expiresAt) {
    if (this.#resetTimer) window.clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      if (variantId && transientVariantStates.get(variantId)?.expiresAt === expiresAt) {
        clearTransientVariantState(variantId, expiresAt);
      }

      const currentButton = variantId ? this.#findCurrentButton(variantId, button) : button;
      this.#setButtonState(currentButton, 'idle');
      this.#announce('', currentButton);
    }, delay);
  }
}

if (!customElements.get('predictive-search-product-actions')) {
  customElements.define('predictive-search-product-actions', PredictiveSearchProductActions);
}
